import type { AdminClosure, ClosureKind, HoursInterval, LocalizedPair } from '@diora/contracts';
import { editablePair } from '@diora/contracts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { OptionalBilingualField } from '@/components/bilingual';
import { FormLoading, FormPage, useDirty } from '@/components/form-page';
import { IntervalRows } from '@/components/hours';
import { Alert, Field, Input, Select } from '@/components/ui';
import { apiGet, apiPatch, apiPost, useAdminMutation } from '@/hooks/admin';
import { qk } from '@/lib/query';
import { BRANCHES_URL } from './branches';

/**
 * A holiday, a Ramadan schedule, or one evening closed for a private event.
 *
 * Its own route under the branch it belongs to, rather than a modal over the
 * hours screen: an override is edited while looking at the seven-day preview it
 * will change, and a dialog covering that preview was exactly the wrong shape.
 */
export function BranchClosureFormPage() {
  const { t } = useTranslation();
  const { id = '', closureId } = useParams();

  const backTo = `/branches/${id}`;

  /*
   * There is no `GET /closures/:id` — the API lists a branch's overrides and
   * nothing else, because they are always read as a set against one schedule.
   * The list is small and already cached by the screen this page was reached
   * from, so it is what the row comes out of.
   */
  const closures = useQuery({
    queryKey: qk.closures(id),
    queryFn: () => apiGet<AdminClosure[]>(`${BRANCHES_URL}/${id}/closures`),
    enabled: Boolean(id) && Boolean(closureId),
  });

  if (!closureId) return <ClosureForm branchId={id} closure={null} />;

  if (closures.isLoading) {
    return <FormLoading title={t('closures.editTitle')} backTo={backTo} />;
  }

  const closure = (closures.data?.data ?? []).find((row) => row.id === closureId);

  if (!closure) {
    return (
      <FormPage title={t('closures.editTitle')} backTo={backTo}>
        <Alert tone="danger">{t('common.noSuchRecord')}</Alert>
      </FormPage>
    );
  }

  return <ClosureForm key={closure.id} branchId={id} closure={closure} />;
}

function ClosureForm({ branchId, closure }: { branchId: string; closure: AdminClosure | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const backTo = `/branches/${branchId}`;

  const [startDate, setStartDate] = useState(closure?.startDate ?? '');
  const [endDate, setEndDate] = useState(closure?.endDate ?? '');
  const [kind, setKind] = useState<ClosureKind>(closure?.kind ?? 'CLOSED');
  const [reason, setReason] = useState<LocalizedPair>(editablePair(closure?.reason));
  const [intervals, setIntervals] = useState<HoursInterval[]>(
    closure?.intervals ?? [{ opensAt: '12:00', closesAt: '18:00', closesNextDay: false }],
  );

  const dirty = useDirty({ startDate, endDate, kind, reason, intervals });

  const save = useAdminMutation(
    () => {
      const body = {
        startDate,
        endDate: endDate || startDate,
        kind,
        reasonAr: reason.ar,
        reasonEn: reason.en,
        ...(kind === 'MODIFIED_HOURS' ? { intervals } : {}),
      };
      return closure
        ? apiPatch(`${BRANCHES_URL}/${branchId}/closures/${closure.id}`, {
            ...body,
            expectedVersion: closure.version,
          })
        : apiPost(`${BRANCHES_URL}/${branchId}/closures`, body);
    },
    {
      successMessage: closure ? t('common.saved') : t('common.created'),
      invalidate: [qk.closures(branchId), qk.branchPreview(branchId), ['branches']],
      onSuccess: () => navigate(backTo),
    },
  );

  return (
    <FormPage
      title={closure ? t('closures.editTitle') : t('closures.newTitle')}
      description={t('closures.subtitle')}
      backTo={backTo}
      dirty={dirty}
      saving={save.isPending}
      canSave={Boolean(startDate)}
      onSave={() => save.mutate(undefined)}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('closures.startDate')} htmlFor="closure-start" required>
          <Input
            id="closure-start"
            type="date"
            dir="ltr"
            className="tabular"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />
        </Field>
        <Field label={t('closures.endDate')} htmlFor="closure-end" hint={t('closures.datesInclusive')}>
          <Input
            id="closure-end"
            type="date"
            dir="ltr"
            className="tabular"
            value={endDate}
            min={startDate}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </Field>
      </div>

      <Field label={t('closures.kind')} htmlFor="closure-kind">
        <Select
          id="closure-kind"
          value={kind}
          onChange={(event) => setKind(event.target.value as ClosureKind)}
        >
          <option value="CLOSED">{t('closures.CLOSED')}</option>
          <option value="MODIFIED_HOURS">{t('closures.MODIFIED_HOURS')}</option>
        </Select>
      </Field>

      {kind === 'MODIFIED_HOURS' && (
        <div className="dio-field">
          <span className="dio-label">{t('hours.intervals')}</span>
          <div className="flex flex-col gap-2">
            <IntervalRows intervals={intervals} onChange={setIntervals} />
          </div>
        </div>
      )}

      <OptionalBilingualField
        label={t('closures.reason')}
        hint={t('closures.reasonHint')}
        value={reason}
        onChange={setReason}
        maxLength={200}
      />

      <Alert tone="info">{t('closures.appliesNote')}</Alert>
    </FormPage>
  );
}
